// BUILD-042 simple benefits (migration 207) — per-employee eligibility review, shown on an
// employee's HR page (HR Administration > employee detail > Benefits) and inside guided setup. HR sets each benefit to
// Eligible (with the date the employee will see) or Not eligible. A plan with no decision is "Needs a decision" - never inferred.
// Eligibility is a REVIEW OUTCOME ONLY — it does not enroll the employee, start coverage, or change payroll.
//
// Cycle 12:
//   * the hire date is shown beside the decisions;
//   * a plan with an HR-configured waiting period offers "Suggested: MM/DD/YYYY (hire date + N days)" as a one-click fill for the eligible date. HR confirms or
//     overrides it. Without a rule (or without a hire date) nothing is suggested, and a plan or hire-date change never rewrites a saved decision;
//   * Eligible REQUIRES a date (inline message, Save disabled until valid; the server enforces it again);
//   * the date and note inputs exist only for Eligible;
//   * in setup, an optional "Not eligible for all" shortcut: confirmation lists the exact undecided plans, then the normal audited per-plan writes run and each
//     result is reported. It never touches a plan that already has a decision.
//
// Every action re-authorizes server-side (hr.benefit.manage, company scope).
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  listHrEmployeeBenefits, setHrEmployeeBenefitEligibility, listHrBenefitEligibilityHistory,
  type HrBenefitEntry, type HrBenefitEligibilityStatus,
} from '../../../api/client';
import { formatLocalDate } from '../hrFormat';
import {
  bulkNotEligibleTargets, draftChanged, draftFromEntry, draftProblem, eligibilityPayload, isApplicable, isDecided, reviewCounts, suggestEligibleDate,
  type EligibilityDraft, type ReviewCounts,
} from '../setup/benefitsModel';

const STATUS_LABEL: Record<HrBenefitEligibilityStatus, string> = {
  not_reviewed: 'Not reviewed', eligible: 'Eligible', not_eligible: 'Not eligible',
};
const STATUS_PILL: Record<HrBenefitEligibilityStatus, string> = {
  not_reviewed: 'bg-gray-100 text-gray-600 border-gray-200',
  eligible: 'bg-green-50 text-green-700 border-green-200',
  not_eligible: 'bg-gray-100 text-gray-700 border-gray-300',
};
const NEEDS_PILL = 'bg-amber-50 text-amber-900 border-amber-300';

export type FlushResult = { ok: true } | { ok: false; message: string; saved?: string; notSaved?: string };
export interface BenefitsSectionProps {
  employeeId: string;
  employeeName?: string;
  /** The employee's hire date (YYYY-MM-DD) when the caller knows it. undefined = not shown (the benefit manager's picker has no hire date); null = shown as not recorded. */
  hireDate?: string | null;
  /** Guided setup: offers the confirmed "Not eligible for all" shortcut. */
  setupMode?: boolean;
  /** Review counts INCLUDING valid unsaved choices (they are saved by Save & continue); null while loading or when the list could not be read. */
  onCounts?: (counts: ReviewCounts | null) => void;
  /** Whether any plan has an unsaved choice. */
  onDirtyChange?: (dirty: boolean) => void;
  /** Registers a function that saves every pending valid choice (Save & continue uses it); null on unmount. */
  bindFlush?: (flush: (() => Promise<FlushResult>) | null) => void;
  /** Called after any decision is written (the step re-reads the server summary). */
  onSaved?: () => void;
}

export function AdminBenefitsEligibilitySection({ employeeId, employeeName, hireDate, setupMode = false, onCounts, onDirtyChange, bindFlush, onSaved }: BenefitsSectionProps) {
  const [benefits, setBenefits] = useState<HrBenefitEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, EligibilityDraft>>({});
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [rowErr, setRowErr] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [bulk, setBulk] = useState<null | { phase: 'confirm' } | { phase: 'running' } | { phase: 'done'; results: Array<{ planId: string; name: string; ok: boolean; message?: string }> }>(null);
  const benefitsRef = useRef<HrBenefitEntry[] | null>(null);
  benefitsRef.current = benefits;
  const draftsRef = useRef(drafts);
  draftsRef.current = drafts;

  const refresh = () =>
    listHrEmployeeBenefits(employeeId)
      .then((d) => { setBenefits(d.benefits); setError(null); return d.benefits; })
      .catch(() => { setBenefits((prev) => prev ?? []); setError('Benefit eligibility could not be loaded right now.'); return null; });
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [employeeId]);

  const draftOf = (b: HrBenefitEntry): EligibilityDraft => drafts[b.planId] ?? draftFromEntry(b);
  const pending = (b: HrBenefitEntry) => !!drafts[b.planId] && draftChanged(b, drafts[b.planId]);

  // Counts INCLUDE valid unsaved choices (Save & continue saves them first), so the footer acknowledgment follows what HR has chosen on screen.
  const overlaid = useMemo(() => (benefits ?? []).map((b) => {
    const d = drafts[b.planId];
    if (d && draftChanged(b, d) && !draftProblem(d)) { const p = eligibilityPayload(d); return { ...b, status: p.status, eligibilityDate: p.eligibilityDate }; }
    return b;
  }), [benefits, drafts]);
  const counts = useMemo(() => (benefits === null || error ? null : reviewCounts(overlaid)), [benefits, error, overlaid]);
  const dirty = (benefits ?? []).some(pending);
  useEffect(() => { onCounts?.(counts); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [counts?.applicable, counts?.decided, counts === null]);
  useEffect(() => { onDirtyChange?.(dirty); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [dirty]);

  function setDraft(b: HrBenefitEntry, patch: Partial<EligibilityDraft>) {
    setDrafts((prev) => ({ ...prev, [b.planId]: { ...(prev[b.planId] ?? draftFromEntry(b)), ...patch } }));
    setRowErr((prev) => { if (!prev[b.planId]) return prev; const n = { ...prev }; delete n[b.planId]; return n; });
  }
  function closeRow(planId: string) {
    setDrafts((p) => { const n = { ...p }; delete n[planId]; return n; });
    setOpen((p) => ({ ...p, [planId]: false }));
    setRowErr((p) => { const n = { ...p }; delete n[planId]; return n; });
  }

  /** Writes ONE plan's decision through the existing per-plan setter. Returns a plain-words failure instead of throwing. */
  async function writeOne(b: HrBenefitEntry, d: EligibilityDraft): Promise<{ ok: true } | { ok: false; message: string }> {
    const problem = draftProblem(d);
    if (problem) return { ok: false, message: problem };
    try { await setHrEmployeeBenefitEligibility(employeeId, b.planId, eligibilityPayload(d)); return { ok: true }; }
    catch (e: any) { return { ok: false, message: e?.message || 'Could not update eligibility.' }; }
  }

  async function saveRow(b: HrBenefitEntry) {
    if (savingId) return;
    setSavingId(b.planId);
    const r = await writeOne(b, draftOf(b));
    if (r.ok) { await refresh(); closeRow(b.planId); onSaved?.(); }
    else setRowErr((p) => ({ ...p, [b.planId]: `Not saved. ${r.message} Your choice is still here.` }));
    setSavingId(null);
  }

  // Save & continue: validate every pending choice FIRST (nothing is half-written because of a typo), then write them one by one.
  useEffect(() => {
    if (!bindFlush) return;
    bindFlush(async () => {
      const list = benefitsRef.current ?? [];
      const todo = list.filter((b) => draftsRef.current[b.planId] && draftChanged(b, draftsRef.current[b.planId]));
      for (const b of todo) {
        const problem = draftProblem(draftsRef.current[b.planId]);
        if (problem) { setOpen((p) => ({ ...p, [b.planId]: true })); setRowErr((p) => ({ ...p, [b.planId]: `Not saved. ${problem} Your choice is still here.` })); return { ok: false, message: `${b.name}: ${problem} Nothing was saved and your choices are still here.`, notSaved: 'benefit decisions' } as FlushResult; }
      }
      const done: string[] = [];
      for (const b of todo) {
        const r = await writeOne(b, draftsRef.current[b.planId]);
        if (!r.ok) {
          await refresh();
          setRowErr((p) => ({ ...p, [b.planId]: `Not saved. ${r.message} Your choice is still here.` }));
          return { ok: false, message: `“${b.name}” was not saved: ${r.message}`, saved: done.length ? done.join(', ') : undefined, notSaved: b.name } as FlushResult;
        }
        done.push(b.name);
      }
      if (todo.length) { await refresh(); for (const b of todo) closeRow(b.planId); onSaved?.(); }
      return { ok: true } as FlushResult;
    });
    return () => bindFlush(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // ── Bulk "Not eligible for all" (setup only) ──────────────────────────────────────────────────────────────────────────────────────
  const bulkTargets = setupMode ? bulkNotEligibleTargets(benefits ?? []) : [];
  const [bulkPlanIds, setBulkPlanIds] = useState<string[]>([]);
  function startBulk() { setBulkPlanIds(bulkTargets.map((b) => b.planId)); setBulk({ phase: 'confirm' }); }
  async function confirmBulk() {
    setBulk({ phase: 'running' });
    // Re-read first: a plan that was decided in the meantime is no longer a target and is never overwritten.
    const fresh = (await refresh()) ?? benefitsRef.current ?? [];
    const targets = bulkNotEligibleTargets(fresh).filter((b) => bulkPlanIds.includes(b.planId));
    const results: Array<{ planId: string; name: string; ok: boolean; message?: string }> = [];
    for (const b of bulkPlanIds.filter((id) => !targets.some((t) => t.planId === id))) {
      const e = fresh.find((x) => x.planId === b);
      const stillThere = e && isApplicable(e);
      results.push({ planId: b, name: e?.name ?? 'This benefit', ok: false, message: stillThere ? 'It already has a decision, so it was left as it is.' : 'It is no longer available, so nothing was saved for it.' });
    }
    for (const b of targets) {
      const r = await writeOne(b, { status: 'not_eligible', date: '', note: '' });
      results.push({ planId: b.planId, name: b.name, ok: r.ok, message: r.ok ? undefined : r.message });
    }
    await refresh(); onSaved?.();
    setBulk({ phase: 'done', results });
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5" data-testid="benefits-eligibility">
      <div className="mb-3">
        <h2 className="text-base font-semibold text-gray-900">Benefits eligibility</h2>
        <p className="text-xs text-gray-500 mt-0.5">A review outcome only. Marking Eligible does not enroll {employeeName || 'this employee'}, start coverage, or change payroll.</p>
        {hireDate !== undefined && (
          <p className="text-sm text-gray-800 mt-2" data-testid="benefits-hire-date">
            <span className="font-medium">Hire date:</span> {hireDate ? formatLocalDate(hireDate) : <span className="text-gray-600">not recorded (no dates can be suggested)</span>}
          </p>
        )}
      </div>

      {error && <div className="bg-red-50 text-sm text-red-700 rounded-md px-3 py-2 mb-3" role="alert">{error}</div>}
      {benefits === null && <p className="text-sm text-gray-400">Loading…</p>}
      {benefits !== null && benefits.length === 0 && !error && (
        <p className="text-sm text-gray-500">No benefits have been set up yet. Add them under Benefit plans.</p>
      )}

      {bulkTargets.length > 0 && bulk === null && (
        <div className="mb-3">
          <button type="button" className={linkBtnCls} onClick={startBulk} data-testid="benefits-bulk-open">Not eligible for all…</button>
          <span className="text-xs text-gray-500 ml-2">({bulkTargets.length} {bulkTargets.length === 1 ? 'benefit has' : 'benefits have'} no decision yet)</span>
        </div>
      )}
      {bulk?.phase === 'confirm' && (
        <div role="group" aria-labelledby="bulk-title" className="mb-3 rounded-md border border-amber-300 bg-amber-50 px-4 py-3" data-testid="benefits-bulk-confirm">
          <p id="bulk-title" className="text-sm font-medium text-amber-950">Mark these {bulkPlanIds.length} {bulkPlanIds.length === 1 ? 'benefit' : 'benefits'} Not eligible for {employeeName || 'this person'}?</p>
          <ul className="mt-1 text-sm text-gray-800 list-disc pl-5" data-testid="benefits-bulk-list">
            {bulkTargets.filter((b) => bulkPlanIds.includes(b.planId)).map((b) => <li key={b.planId}>{b.name}</li>)}
          </ul>
          <p className="text-xs text-gray-700 mt-2">Each one is saved as its own decision and appears in the activity history. Benefits that already have a decision are not listed and are not changed.</p>
          <div className="flex gap-2 mt-3">
            <button type="button" className={primaryBtnCls} onClick={confirmBulk} data-testid="benefits-bulk-confirm-button">Mark these Not eligible</button>
            <button type="button" className={secondaryBtnCls} onClick={() => setBulk(null)} data-testid="benefits-bulk-cancel">Cancel</button>
          </div>
        </div>
      )}
      {bulk?.phase === 'running' && <p role="status" className="mb-3 text-sm text-gray-600">Saving each decision…</p>}
      {bulk?.phase === 'done' && (
        <div role="status" className="mb-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3" data-testid="benefits-bulk-results">
          <ul className="text-sm space-y-0.5">
            {bulk.results.map((r) => (
              <li key={r.planId} className={r.ok ? 'text-gray-800' : 'text-red-700'}>{r.ok ? `${r.name}: saved as Not eligible.` : `${r.name}: not saved. ${r.message}`}</li>
            ))}
          </ul>
          <button type="button" className={`${linkBtnCls} mt-2`} onClick={() => setBulk(null)}>Done</button>
        </div>
      )}

      <div className="divide-y divide-gray-100">
        {(benefits ?? []).map((b) => (
          <BenefitEligibilityRow
            key={b.planId}
            benefit={b}
            hireDate={hireDate ?? null}
            draft={draftOf(b)}
            hasDraft={!!drafts[b.planId]}
            isOpen={!!open[b.planId] || (isApplicable(b) && !isDecided(b))}
            saving={savingId === b.planId}
            busyElsewhere={!!savingId && savingId !== b.planId}
            error={rowErr[b.planId] ?? null}
            onDraft={(patch) => setDraft(b, patch)}
            onEdit={() => setOpen((p) => ({ ...p, [b.planId]: true }))}
            onClose={() => closeRow(b.planId)}
            onSave={() => saveRow(b)}
          />
        ))}
      </div>
    </div>
  );
}

function BenefitEligibilityRow({ benefit, hireDate, draft, hasDraft, isOpen, saving, busyElsewhere, error, onDraft, onEdit, onClose, onSave }: {
  benefit: HrBenefitEntry; hireDate: string | null; draft: EligibilityDraft; hasDraft: boolean; isOpen: boolean; saving: boolean; busyElsewhere: boolean; error: string | null;
  onDraft: (patch: Partial<EligibilityDraft>) => void; onEdit: () => void; onClose: () => void; onSave: () => void;
}) {
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState<Array<{ historyId: string; status: string; eligibilityDate: string | null; note: string | null; changedByEmployeeId: string; changedAt: string }> | null>(null);
  const needs = isApplicable(benefit) && !isDecided(benefit);

  async function loadHistory() {
    if (!benefit.eligibilityId) { setHistory([]); setShowHistory(true); return; }
    setShowHistory(true);
    try { const d = await listHrBenefitEligibilityHistory(benefit.eligibilityId); setHistory(d.history); }
    catch { setHistory([]); }
  }

  const problem = (hasDraft || needs) && draft.status === 'eligible' ? draftProblem(draft) : null;
  const canSave = draftProblem(draft) === null && draftChanged(benefit, draft);
  const suggestion = suggestEligibleDate(hireDate, benefit.waitingPeriodDays);
  const ruleButNoHire = typeof benefit.waitingPeriodDays === 'number' && !hireDate;
  const dateId = `elig-date-${benefit.planId}`;
  const msgId = `elig-msg-${benefit.planId}`;

  if (!isOpen) {
    return (
      <div className="py-3 flex items-start justify-between gap-3" data-testid={`benefit-row-${benefit.planId}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">{benefit.name}</span>
            <span className={`text-xs border rounded px-1.5 py-0.5 ${STATUS_PILL[benefit.status]}`}>{STATUS_LABEL[benefit.status]}</span>
            {!benefit.published && <span className="text-xs text-gray-600 border border-gray-200 bg-gray-50 rounded px-1.5 py-0.5">Not visible to employees</span>}
            {/* Oct 24: an Eligible decision carries the date the employee is shown ("Eligible starting <date>"). */}
            {benefit.status === 'eligible' && benefit.eligibilityDate && <span className="text-xs text-gray-700" data-testid="eligible-starting">Eligible starting {formatLocalDate(benefit.eligibilityDate)}</span>}
            {benefit.status !== 'eligible' && benefit.eligibilityDate && <span className="text-xs text-gray-500">as of {benefit.eligibilityDate}</span>}
          </div>
          {benefit.note && <p className="text-xs text-gray-500 mt-1">{benefit.note}</p>}
          {showHistory && (
            <div className="mt-2 text-xs text-gray-500">
              {history === null && <span>Loading history…</span>}
              {history !== null && history.length === 0 && <span>No prior changes recorded.</span>}
              {history !== null && history.length > 0 && (
                <ul className="space-y-0.5">
                  {history.map((h) => (
                    <li key={h.historyId}>Was {STATUS_LABEL[(h.status as HrBenefitEligibilityStatus)] ?? h.status}{h.eligibilityDate ? ` (${h.eligibilityDate})` : ''} · changed {new Date(h.changedAt).toLocaleString()}</li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button type="button" className={secondaryBtnCls} onClick={onEdit} aria-label={`Change eligibility for ${benefit.name}`}>Change</button>
          {!showHistory && <button type="button" className="text-xs text-[#4AABDE] hover:underline self-center" onClick={loadHistory}>History</button>}
        </div>
      </div>
    );
  }

  const name = `elig-${benefit.planId}`;
  return (
    <div className="py-3" data-testid={`benefit-row-${benefit.planId}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <span className="text-sm font-medium text-gray-900">{benefit.name}</span>
        {needs
          ? <span className={`text-xs border rounded px-1.5 py-0.5 ${NEEDS_PILL}`} data-testid={`needs-decision-${benefit.planId}`}>{benefit.status === 'eligible' ? 'Needs a date' : 'Needs a decision'}</span>
          : <span className={`text-xs border rounded px-1.5 py-0.5 ${STATUS_PILL[benefit.status]}`}>{STATUS_LABEL[benefit.status]}</span>}
        {!benefit.published && <span className="text-xs text-gray-600 border border-gray-200 bg-gray-50 rounded px-1.5 py-0.5">Not visible to employees</span>}
      </div>
      <fieldset className="min-w-0">
        <legend className="sr-only">Eligibility for {benefit.name}</legend>
        <div className="flex flex-wrap gap-x-6 gap-y-1">
          <label className="inline-flex items-center gap-2 text-sm text-gray-800">
            <input type="radio" name={name} value="eligible" checked={draft.status === 'eligible'} onChange={() => onDraft({ status: 'eligible' })} data-testid={`elig-eligible-${benefit.planId}`} />
            Eligible
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-800">
            <input type="radio" name={name} value="not_eligible" checked={draft.status === 'not_eligible'} onChange={() => onDraft({ status: 'not_eligible' })} data-testid={`elig-not-eligible-${benefit.planId}`} />
            Not eligible
          </label>
        </div>
      </fieldset>

      {draft.status === 'eligible' && (
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-gray-600 block mb-1" htmlFor={dateId}>Eligible starting (required)</label>
            <input id={dateId} type="date" className={`${inputCls}${problem ? ' border-red-400' : ''}`} value={draft.date} onChange={(e) => onDraft({ date: e.target.value })}
              required aria-invalid={problem ? true : undefined} aria-describedby={problem ? msgId : undefined} data-testid="eligibility-date-input" />
            {suggestion && (
              <button type="button" className={`${linkBtnCls} mt-1 text-left`} onClick={() => onDraft({ date: suggestion.date })} data-testid={`elig-suggestion-${benefit.planId}`}>
                {suggestion.label}{draft.date === suggestion.date ? ' ✓ in use' : ' — use this date'}
              </button>
            )}
            {!suggestion && ruleButNoHire && <p className="text-[11px] text-gray-500 mt-1">This benefit has a waiting period, but no hire date is on record, so no date is suggested.</p>}
            {problem && <p id={msgId} role="alert" className="text-xs text-red-700 mt-1" data-testid={`elig-message-${benefit.planId}`}>{problem}</p>}
          </div>
          <div>
            <label className="text-xs text-gray-600 block mb-1" htmlFor={`elig-note-${benefit.planId}`}>Note (optional, HR-internal)</label>
            <input id={`elig-note-${benefit.planId}`} className={inputCls} value={draft.note} onChange={(e) => onDraft({ note: e.target.value })} placeholder="Why this decision, if useful" />
          </div>
        </div>
      )}

      {error && <p role="alert" className="text-sm text-red-600 mt-2" data-testid={`elig-error-${benefit.planId}`}>{error}</p>}
      <div className="flex gap-2 mt-3">
        <button type="button" className={primaryBtnCls} disabled={saving || busyElsewhere || !canSave} onClick={onSave} data-testid={`elig-save-${benefit.planId}`}>{saving ? 'Saving…' : 'Save'}</button>
        {(!needs || hasDraft) && <button type="button" className={secondaryBtnCls} disabled={saving} onClick={onClose}>Cancel</button>}
      </div>
    </div>
  );
}

const inputCls = 'border border-gray-300 rounded px-3 py-1.5 text-sm w-full bg-white';
const primaryBtnCls = 'text-sm font-medium bg-[#4AABDE] text-white rounded px-3 py-1.5 hover:bg-[#3b96c4] disabled:opacity-50';
const secondaryBtnCls = 'text-sm font-medium border border-gray-300 text-gray-700 rounded px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50';
const linkBtnCls = 'text-xs text-[#176A9B] underline hover:text-[#0d4a70]';
